import { DDSIconDefinition } from '@driftt/dds'

export interface ActivityContext {
  pageUrl?: string
  url?: string
  pageTitle?: string
  agentParticipants?: number[]
  endUserParticipants?: number[]
  participantIds?: number[]
  conversationPreview?: string
  campaignId?: number
  playbookId?: number
  playbookName?: string
  subject?: string
  title?: string
  thumbnailUrl?: string
  id?: string
  fromOutreach?: boolean
  meetingType?: string
  slotStart?: number
  slug?: string
  percentViewed?: number
  taskContext?: any
  referrer?: string
  driftSfdcTask?: DriftSfdcTask
  pageVisits?: { url?: string; title?: string }[]
  endUserParticipantObjects?: ActivityEndUser[]
  emailInteractionData?: EmailInteractions
  emailPreviewData?: EmailActivityPreview
  schedulingEvent?: { meetingType: string; slotStart?: number }
  videoEmbedLink?: string
  prompt?: string
  signal?: SalesloftSignal
}

export interface EmailInteractions {
  opens: number
  clicks: number
}

export interface EmailActivityPreview {
  id?: string
  subject?: string
  description?: string
}

export interface DriftSfdcTask {
  subject?: string
  description?: string
  taskId?: string
}

export enum ContactType {
  UNKNOWN = 'UNKNOWN',
  KNOWN = 'KNOWN',
  ALL = 'ALL'
}

export enum ActivityType {
  PAGE_VIEW = 'PAGE_VIEW',
  CONVERSATION = 'CONVERSATION',
  MEETING_BOOKED = 'MEETING_BOOKED',
  MEETING_CANCELED = 'MEETING_CANCELED',
  MEETING_RESCHEDULED = 'MEETING_RESCHEDULED',
  MEETING_RECORDED = 'MEETING_RECORDED',
  VIDEO = 'VIDEO',
  EMAIL_OPEN = 'EMAIL_OPEN',
  EMAIL_CLICK = 'EMAIL_CLICK',
  EMAIL_SEND = 'EMAIL_SEND',
  END_USER_IDENTIFIED = 'END_USER_IDENTIFIED',
  EMAIL_CLICK_SFDC = 'EMAIL_CLICK_SFDC',
  EMAIL_OPEN_SFDC = 'EMAIL_OPEN_SFDC',
  EMAIL_INBOUND = 'EMAIL_INBOUND',
  WEBINAR = 'WEBINAR',
  SMS_INBOUND = 'SMS_INBOUND',
  OTHER_CHATBOT_CONVERSATION = 'OTHER_CHATBOT_CONVERSATION',
  FILLED_OUT_FORM = 'FILLED_OUT_FORM',
  UNSUBSCRIBED = 'UNSUBSCRIBED',
  CONTRACT_REQUEST = 'CONTRACT_REQUEST',
  WIDGET_CONTENT_CLICK = 'WIDGET_CONTENT_CLICK',
  WIDGET_SEARCH_SEND = 'WIDGET_SEARCH_SEND',
  ADDED = 'ADDED',
  SIGNAL = 'SIGNAL'
}

export const activityTypeToString = (activity: ActivityType) => {
  switch (activity) {
    case ActivityType.PAGE_VIEW:
      return 'site visit'
    case ActivityType.CONVERSATION:
      return 'conversation'
    case ActivityType.MEETING_BOOKED:
      return 'meeting booked'
    case ActivityType.MEETING_CANCELED:
      return 'meeting canceled'
    case ActivityType.MEETING_RESCHEDULED:
      return 'meeting rescheduled'
    case ActivityType.VIDEO:
      return 'video view'
    case ActivityType.EMAIL_OPEN:
      return 'email open'
    case ActivityType.EMAIL_CLICK:
      return 'email click'
    case ActivityType.EMAIL_SEND:
      return 'email send'
    case ActivityType.END_USER_IDENTIFIED:
      return 'provided email'
    case ActivityType.EMAIL_INBOUND:
      return 'email inbound'
    case ActivityType.WEBINAR:
      return 'webinar'
    case ActivityType.SMS_INBOUND:
      return 'sms inbound'
    case ActivityType.FILLED_OUT_FORM:
      return 'filled out form'
    case ActivityType.UNSUBSCRIBED:
      return 'unsubscribed'
    case ActivityType.CONTRACT_REQUEST:
      return 'contract request'
  }
}

export enum ScoredActivityType {
  CONVERSATION = 'CONVERSATION',
  VIDEO = 'VIDEO',
  PAGE_VIEW = 'PAGE_VIEW',
  HIGH_INTENT_PAGE_VIEW = 'HIGH_INTENT_PAGE_VIEW',
  NON_HOMEPAGE_VIEW = 'NON_HOMEPAGE_VIEW',
  HOMEPAGE_VIEW = 'HOMEPAGE_VIEW',
  EMAIL_OPEN = 'EMAIL_OPEN',
  EMAIL_CLICK = 'EMAIL_CLICK',
  MEETING_BOOKED = 'MEETING_BOOKED',
  MEETING_CANCELED = 'MEETING_CANCELED',
  MEETING_RESCHEDULING = 'MEETING_RESCHEDULING',
  MEETING_OTHER = 'MEETING_OTHER',
  END_USER_IDENTIFIED = 'END_USER_IDENTIFIED'
}

export interface ScoredActivity {
  activityType: string
  createdAt: number
  activityScore: number
  endUserId: number
  name?: string
  email?: string
  role?: string
  activityContext: ActivityContext
  companyData?: CompanyData
  endUserData?: ActivityEndUser
  id: number
}
export interface CompanyData {
  logo?: string
  name?: string
  domain?: string
  about?: string
  industry?: string
  revenue?: string
  alexaGlobalRank?: number
  alexaUsRank?: number
  employeeCount?: string
  employeeCountRange?: string
  location?: string
  city?: string
  state?: string
  country?: string
  linkedinHandle?: string
  twitterHandle?: string
  url?: string
  driftIntelSources: {
    [key: string]: string[]
  }
}

export interface Account {
  accountId: string
  name: string
}
export interface AccountActivity {
  accountId: string
  companyData: CompanyData
  account?: Account
  mostRecentActivity: Activity
  numberOfPageVisits: number
  engagementScore: number
  normalizedEngagementScore: number
  accountFitScore?: number
  accountScoredActivity?: AccountScoredActivity
  driftSfdcTask?: LastTouch
  featureWeights: ScoreBreakdown
}

export interface ActivityEndUser {
  id: number
  type?: string
  name?: string
  role?: string
  email?: string
  linkedinHandle?: string
  avatarUrl: string
  locationCity: string
  locationState: string
  locationStateCode: string
  locationCountry: string
  locationCountryCode: string
}

export enum LastTouchType {
  EMAIL_SEND = 'EMAIL_SEND',
  EMAIL_OUTBOUND = 'EMAIL_OUTBOUND',
  AUTOMATED_ACCOUNT_REMINDER = 'AUTOMATED_ACCOUNT_REMINDER',
  SMS_INBOUND = 'SMS_INBOUND',
  SMS_OUTBOUND = 'SMS_OUTBOUND',
  CALL_TASK = 'CALL_TASK',
  LINKED_IN_MESSAGE = 'LINKED_IN_MESSAGE',
  LINKED_IN_CONNECTION_REQUEST = 'LINKED_IN_CONNECTION_REQUEST',
  OTHER_CHATBOT_CONVERSATION = 'OTHER_CHATBOT_CONVERSATION'
}

export interface LastTouch {
  orgId: number
  taskId: number
  activityAt: number
  taskCategory: LastTouchType | null
  insertedAt: number
  type: string
  subType: string
  ownerSfdcId: number
  ownerDriftId: number | null
  ownerEmail: string
  ownerName: string
  sfdcAccountId: number | null
  driftAccountId: string
  subject: string
  description: string
  buyer: {
    endUserId: number
    role: string
    email: string
    name: string | null
  }
}

export interface FormatLastTouched {
  icon: DDSIconDefinition
  action: string
  actionType: string
  union: string
  sender: string
  recipient: string
  showBlank?: boolean
}

export interface Activity {
  id: number
  orgId: number
  endUserId: number
  accountId: string
  accountOwnerId: number
  createdAt: number
  activityType: ActivityType
  endUserData: ActivityEndUser
  companyData: CompanyData
  accountData?: Account
  activityContext: ActivityContext
  score: number
  driftSfdcTask?: LastTouch
}

export interface ActivityTimeline {
  orgId: number
  accountId: string
  endUsers: ActivityEndUser[]
  companyData: CompanyData
  accountData?: Account
  activityEvents: ActivityEvent[]
}

export interface ActivityEvent {
  endUserId: number
  activityType: ActivityType
  activityContext: ActivityContext
  createdAt: number
}

export interface ActivityForContact {
  accountData: CompanyData
  activityContext: ActivityContext
  activityScore: number
  createdAt: number
  activityType: ActivityType
  email: string
  endUserId: number
  name?: string
  role?: string
}

export interface AccountScoredActivity {
  orgId: number
  accountId: string
  historicalScores: number[]
  lastActivityId: number
  normalizedScore: number
  score: number
  trend: number
  topActivities: AIScoringTopActivities[]
  recommendation: number
}

export interface ScoreBreakdown {
  Industry: string
  Country: string
  EmployeeCount: string
}

export interface AIScoringTopActivities {
  activity: ActivityType | string
  source: 'DRIFT' | 'SALESFORCE'
  activityId: number
  categorizedRole: string
  intent: boolean
  receivedAt: number
  attributes: {
    pageTitle?: string
    pageUrl?: string
    categorizedUrl?: string
    endUserName?: string
    endUserId?: number
    endUserEmail?: string
    conversationId?: number
    videoUrl?: string
    videoTitle?: string
    conversationPreview?: string
  }
}

export interface ContactWithActivites {
  endUserId: number
  endUserData: ActivityEndUser
  contactName?: string
  companyData: CompanyData
  activityCount: number
  timeOfLastEngagement: number
  liftAIIntent?: string
  liftAIScore?: string
}

export enum AccountActivitySortValue {
  ENGAGEMENT_SCORE = 'ENGAGEMENT_SCORE',
  MOST_RECENT = 'MOST_RECENT',
  LEAST_RECENT = 'LEAST_RECENT',
  CONTACT_NAME = 'CONTACT_NAME',
  CONTACT_EMAIL = 'CONTACT_EMAIL',
  NUM_ACTIVITIES = 'NUM_ACTIVITIES',
  CONTACT_ROLE = 'CONTACT_ROLE',
  LOCATION = 'LOCATION',
  ACCOUNT_NAME = 'ACCOUNT_NAME',
  DEAL_NAME = 'DEAL_NAME',
  DEAL_AMOUNT = 'DEAL_AMOUNT',
  DEAL_PROBABILITY = 'DEAL_PROBABILITY',
  DEAL_STAGE = 'DEAL_STAGE',
  DEAL_FORECAST_CATEGORY = 'DEAL_FORECAST_CATEGORY',
  DEAL_TYPE = 'DEAL_TYPE',
  DEAL_OWNER = 'DEAL_OWNER',
  TRENDING = 'TRENDING'
}

export interface Opportunity {
  opportunityName: string | null
  ownerName: string | null
  opportunityCloseDate: string | null
  opportunityStageName: string | null
  opportunityAmount: string | null
  opportunityType: string | null
}

export interface InsightOpportunity {
  accountId: string
  accountName: string
  amount: number
  closeDate: number
  description: string
  forecastCategoryName: string
  isClosed: boolean
  isWon: boolean
  leadSource: string
  name: string
  nextStep: string
  opportunityId: string
  orgId: number
  ownerId: number
  probability: number
  source: string
  stageName: string
  type: string
  score: number
}

export interface Insight {
  id: number
  orgId: number
  insightType: string
  accountId: string
  accountData?: Account
  body: any
  companyData: CompanyData
  scoredAccount?: AccountActivity
  companyName: string
  lastActivityAt: number
  comments: Comment[]
  numActivities?: number
}

export interface Comment {
  orgId: number
  resourceId: string
  commentId: string
  parentCommentId?: string
  content: string
  taggedUsers: string[]
  createdAt: number
  deleted: boolean
  userId: number
  userType: string
  children?: Comment[]
}

export interface ActivityGroupByAccountAndEndUsersBody {
  activityGroupsByEndUser: ActivityGroupByEndUser[]
  activityCounts: ActivityCounts
  contactCounts: {
    knownContacts: number
    siteVisitors: number
  }
  endUserIds: number[]
}

export interface Contact {
  city: string
  contactId: number
  country: string
  email: string
  employmentTitle: string
  endUserIds: number[]
  isKnownContact: boolean
  name: string
  role: string
  state: string
  avatarUrl: string
}

export type ActivityGroupByEndUser = {
  endUserId: number
  activityList: ActivityForContact[]
  endUserData: ActivityEndUser
  lastActivityAt: number
} & ActivityEndUser

export interface ActivityCounts {
  CONVERSATION?: number
  EMAIL_CLICK?: number
  EMAIL_OPEN?: number
  EMAIL_SEND?: number // TODO: Remove me
  END_USER_IDENTIFIED?: number
  MEETING_BOOKED?: number
  MEETING_CANCELED?: number
  MEETING_RESCHEDULED?: number
  PAGE_VIEW?: number
  VIDEO?: number
}

export interface EngagementByWeek {
  weekStart: string
  engagementByType: any
}

export interface EngagementByDay {
  startOfDay: number
  engagementByType: any
}

export interface EngagementSummary {
  orgId: number
  accountId: string
  engagementByWeek: EngagementByWeek[]
  engagementByDay: EngagementByDay[]
}

export interface MeetingRecordingBody {
  orgId: number
  videoId: number
  driftUserId: number
  zoomUUID?: string
  title?: string
  videoPublicUrl?: string
  videoEmbedLink: string
  schedulingEvent?: {
    meetingType: string
    zoomInfo?: {
      topic: string
    }
  }
  participants: ActivityEndUser[]
}

export type EndUserPropertySource =
  | 'LEGACY'
  | 'DEMANDBASE_IP_LOOKUP'
  | 'DEMANDBASE_DOMAIN_LOOKUP'
  | 'SALESFORCE'
  | 'HUBSPOT'
  | 'CLEARBIT_ENRICHMENT'
  | 'CLEARBIT_REVEAL'
  | 'DRIFT_BOT'
  | 'GEO_IP'
  | 'DRIFT_UTM'
  | 'MADKUDU'
  | 'CLEARBIT_DISCOVERY'
  | 'SALESFORCE_LEAD'
  | 'SALESFORCE_CONTACT'
  | 'SALESFORCE_ACCOUNT'
  | 'SIX_SENSE'
  | 'FULL_CONTACT'
  | 'DRIFT_BOOTSTRAP'
  | 'ZOOM_INFO'
  | 'SIX_SENSE_OEM'
  | 'ZOOM_INFO_OEM'
  | 'CLEARBIT_REVEAL_OEM'
  | 'CLEARBIT_ENRICHMENT_V2'
  | 'CLEARBIT_REVEAL_V2'
  | 'CLEARBIT_ENRICHMENT_V2_OEM'
  | 'CLEARBIT_REVEAL_V2_OEM'
  | 'DRIFT_INTEL_V2'
  | 'CRUNCHBASE_OEM'
  | 'MARKETO_LIST'
  | 'PARDOT_LIST'

export interface SalesloftSignal {
  description?: string,
  type: string,
  occurred_at: number,
  indicators: Indicator[]
}

export interface Indicator {
  description?: string,
  key: string,
  metadata: {[key: string]: unknown}
}